import { Component, forwardRef, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, NG_VALUE_ACCESSOR, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { GenericModalService, ModalSettingsTemplate } from '../generic-modal/generic-modal.service';
import { DurationOption } from './duration-option';
import { AsyncPipe } from '@angular/common';

const defaultDurationOptions: DurationOption[] = [
    { seconds: 60, text: '1 minute', custom: false },
    { seconds: 180, text: '3 minutes', custom: false },
    { seconds: 300, text: '5 minutes', custom: false },
    { seconds: 600, text: '10 minutes', custom: false },
    { seconds: 1200, text: '20 minutes', custom: false },
    { seconds: 1800, text: '30 minutes', custom: false },
    { seconds: 3600, text: '1 hour', custom: false }
];

@Component({
    selector: 'ep-duration-picker',
    templateUrl: './duration-picker.component.html',
    styleUrls: ['./duration-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DurationPickerComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, ReactiveFormsModule, AsyncPipe]
})
export class DurationPickerComponent implements ControlValueAccessor, OnInit {
    @ViewChild('customDialog') customDialog: any;

    @Input() disabled = false;
    @Input() innerClass = '';
    @Input() invalid = false;

    formData: UntypedFormGroup;
    dialogData: UntypedFormGroup;

    private defaultOptions = new BehaviorSubject<DurationOption[]>(defaultDurationOptions);
    defaultOptions$ = this.defaultOptions.asObservable();

    private customOption = new BehaviorSubject<DurationOption>(null);
    customOption$ = this.customOption.asObservable();

    options$ = combineLatest([this.defaultOptions$, this.customOption$]).pipe(
        map(([defos, cusos]) => {
            if (cusos && defos.findIndex(e => e.seconds === cusos.seconds) < 1) {
                defos = [...defos]; // Clone array; don't modify the original
                defos.push(cusos);
                defos.sort((e1, e2) => e1.seconds - e2.seconds);
            }
            return defos;
        })
    );

    onChange = (_: any) => { };
    onTouched = () => { };

    constructor(
        private fb: UntypedFormBuilder,
        private modalService: GenericModalService) {
    }

    ngOnInit(): void {
        this.formData = this.fb.group({
            duration: 0
        });

        this.dialogData = this.fb.group({
            customInterval: [0, [Validators.required, Validators.min(30)]]
        });

        this.formData.valueChanges.subscribe((value: any) => {
            this.onChange(value.duration);
            this.updateOptions(value.duration);
        });
    }

    get fcDuration(): UntypedFormControl {
        return this.formData.get('duration') as UntypedFormControl;
    }

    get fcCustomInterval(): UntypedFormControl {
        return this.dialogData.get('customInterval') as UntypedFormControl;
    }

    async addCustomInterval(): Promise<void> {
        const newValue = await this.showCustomIntervalDialog(this.fcDuration.value);
        if (newValue) {
            this.updateOptions(newValue);
            this.fcDuration.setValue(newValue);
        }
    }

    async showCustomIntervalDialog(initialValue?: string): Promise<string> {
        try {
            this.dialogData.setValue({
                customInterval: initialValue || '120'
            });
            await this.modalService.openTemplate(this.customDialog, new ModalSettingsTemplate('sm', 'static'));
            return this.fcCustomInterval.value;
        }
        catch {
            // Intentionally left empty; called when dialog is canceled
            return undefined;
        }
    }

    updateOptions(newValue: any): void {
        const seconds = +newValue;
        if (seconds) {
            this.options$.pipe(take(1)).subscribe(options => {
                const found = options.find(e => e.seconds === seconds);
                if (found) {
                    if (!found.custom) {
                        this.customOption.next(null);
                    }
                }
                else {
                    const cusos = { seconds, text: `${seconds} seconds *`, custom: true };
                    this.customOption.next(cusos);
                }
            });
        }
        else {
            this.customOption.next(null);
        }
    }

    // Begin ControlValueAccessor //

    writeValue(newValue: any): void {
        this.updateOptions(newValue);
        this.formData.setValue({ duration: newValue }, { emitEvent: false });
    }

    registerOnChange(fn: (_: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        if (isDisabled) {
            this.formData.disable({ onlySelf: true, emitEvent: false });
        }
        else {
            this.formData.enable({ onlySelf: true, emitEvent: false });
        }
    }

    // End ControlValueAccessor //
}
